import React, { memo, useCallback, useContext, useState } from "react";

import { useTranslation } from "react-i18next";
import moment from "moment-timezone";

import { Disclosure } from "@headlessui/react";
import { useReimbursement } from "@hooks/reimbursments/useReimbursement";
import { useReduxSelector } from "@hooks/useReduxSelector";
import {
  CaretClosure,
  SvgAmount,
  SvgBlackTrash,
  SvgIndicatorResponse,
  SvgMenuExpenses
} from "@routes/IconeSvg";

import AddButton from "@components/buttons/AddButton";
import DotLoader from "@components/tailwindComponents/DotLoader";
import EmptyData from "@components/tailwindComponents/EmptyData";
import Menu from "@components/tailwindComponents/Menu";

import { getAmount } from "@helpers/_functions";

import ModalMileinageRequest from "../../components/modals/MileageRequest";
import ModalNewRequest from "../../components/modals/NewRequest";
import { useExpenses } from "../../utils/hooks";
import { ExpensesContext } from "../context";
import Header from "./Header";
import Items from "./Items";

const ExpenseColumns = () => (
  <colgroup>
    <col className="arh-w-[17%]" />
    <col className="arh-w-[12%]" />
    <col className="arh-w-[15%]" />
    <col className="arh-w-[14%]" />
    <col className="arh-w-[14%]" />
    <col className="arh-w-[13%]" />
    <col className="arh-w-[15%]" />
  </colgroup>
);

const Index = memo(function Index() {
  const reimbursmentsList = useContext(ExpensesContext);
  const filterPeriod = useReduxSelector(
    ({ reimbursments }) => reimbursments.employerFilter.period
  );
  const { filterLoading, getDataFilter, reimbursmentsFiltered, drafts } =
    useExpenses(reimbursmentsList);
  const { t, i18n } = useTranslation("expenses.reimbursment");
  const [showModalRequest, setShowNewRequest] = useState(false);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [showMileageReimbursment, setShowMileageReimbursment] = useState(false);
  const { onDelete } = useReimbursement();
  const onClickNewRequest = useCallback(() => {
    setShowNewRequest((showModalRequest) => !showModalRequest);
  }, []);
  const onAddClickMileageReimbursment = useCallback(() => {
    setShowMileageReimbursment(
      (showMileageReimbursment) => !showMileageReimbursment
    );
  }, []);
  const onOpenDraft = useCallback((draft) => {
    setSelectedDraft(draft);
    setShowNewRequest(true);
  }, []);
  const onCloseDraft = useCallback(() => {
    setSelectedDraft(null);
    setShowNewRequest(false);
  }, []);
  const onDeleteDraft = useCallback(
    (draft) => {
      onDelete(draft?._id);
    },
    [onDelete]
  );

  return (
    <>
      <Header
        t={t}
        setDataFilter={getDataFilter}
        filtersLists={{ period: filterPeriod }}
        isOpen={true}
      />

      <div className="arh-flex arh-items-center arh-space-x-8">
        <AddButton
          label={t("Remboursement")}
          onClick={onClickNewRequest}
          color="arh-bg-greenColor hover:arh-bg-darkGreenColor"
        />
        <AddButton
          label={t("Remboursement kilométrique")}
          onClick={onAddClickMileageReimbursment}
          color="arh-bg-greenColor hover:arh-bg-darkGreenColor"
        />
      </div>

      <Disclosure defaultOpen={false}>
        {({ open }) => (
          <div className="arh-mb-4 arh-overflow-hidden arh-rounded-md arh-border arh-border-solid arh-border-greyBorderBtn arh-bg-white ">
            <Disclosure.Button className="arh-flex arh-w-full arh-items-stretch arh-justify-between arh-border-0 arh-bg-white arh-p-0 arh-text-left">
              <span className="arh-flex arh-flex-1 arh-items-center arh-space-x-2 arh-px-3 arh-py-2 arh-font-poppins arh-text-[13px] arh-font-medium arh-leading-[100%] arh-text-darkColor">
                <span className="arh-flex arh-h-5 arh-w-5 arh-items-center arh-justify-center arh-rounded-full arh-bg-[#E6F8EF] arh-font-poppins arh-text-[10px] arh-font-medium arh-leading-none arh-text-greenColor">
                  {drafts?.length || 0}
                </span>
                <span>{t("Brouillons en attente")}</span>
              </span>
              <span className="arh-flex arh-w-8 arh-items-center arh-justify-center">
                <CaretClosure
                  className={`${open ? "arh-rotate-0" : "-arh-rotate-90"}`}
                  aria-hidden="true"
                />
              </span>
            </Disclosure.Button>
            <Disclosure.Panel className="arh-relative arh-overflow-x-auto arh-pb-3">
              <table className="arh-w-full arh-min-w-[1100px] arh-table-fixed arh-font-poppins">
                <ExpenseColumns />
                <thead className="arh-bg-greyColor">
                  <tr className="arh-border-x-0 arh-border-b arh-border-t-0 arh-border-solid arh-border-borderTable">
                    {[
                      "Catégorie",
                      "Montant",
                      "Date",
                      "Description",
                      "Commentaire",
                      "Fichier joint",
                      "Statut"
                    ].map((label) => (
                      <th
                        key={label}
                        className="arh-px-4 arh-py-3.5 arh-text-left arh-font-poppins arh-text-xs arh-font-semibold arh-capitalize arh-text-darkColor"
                      >
                        {t(label)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {drafts?.length ? (
                    drafts.map((draft) => (
                      <tr
                        key={draft._id}
                        className="arh-border-x-0 arh-border-b arh-border-t-0 arh-border-solid arh-border-borderTable"
                      >
                        <td className="arh-px-4 arh-py-3 arh-font-poppins arh-text-xs arh-font-medium arh-text-darkGreyColor">
                          {draft?.category?.name || "-"}
                        </td>
                        <td className="arh-px-4 arh-py-3 arh-font-poppins arh-text-xs arh-font-medium arh-text-darkGreyColor">
                          <div className="arh-flex arh-items-center arh-justify-between arh-space-x-2">
                            <span className="arh-truncate">
                              {draft?.devise || "CAD"} {getAmount(draft)}
                            </span>
                            <span className="arh-shrink-0">{SvgAmount()}</span>
                          </div>
                        </td>
                        <td className="arh-whitespace-nowrap arh-px-4 arh-py-3 arh-font-poppins arh-text-xs arh-font-medium arh-text-darkGreyColor">
                          {draft?.date && moment(draft.date, "YYYY-MM-DD").isValid()
                            ? moment(draft.date, "YYYY-MM-DD")
                              .locale(i18n.language)
                              .format("LL")
                            : "-"}
                        </td>
                        <td className="arh-truncate arh-px-4 arh-py-3 arh-font-poppins arh-text-xs arh-font-medium arh-text-darkGreyColor">
                          {draft?.description || "-"}
                        </td>
                        <td className="arh-truncate arh-px-4 arh-py-3 arh-font-poppins arh-text-xs arh-font-medium arh-text-darkGreyColor">
                          <div className="arh-flex arh-items-center arh-justify-between arh-space-x-1">
                            <span className="arh-truncate">
                              {draft?.commentSended || "-"}
                            </span>
                            {draft?.commentReceived && (
                              <span className="arh-shrink-0">
                                {SvgIndicatorResponse()}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="arh-px-4 arh-py-3 arh-font-poppins arh-text-xs arh-font-medium arh-text-darkGreyColor">
                          <span className="arh-flex arh-items-center arh-space-x-1 arh-truncate">
                            {draft?.files?.length ? (
                              <>
                                <span className="arh-shrink-0">
                                  {SvgMenuExpenses()}
                                </span>
                                <span className="arh-truncate">
                                  {draft.files[0]?.filename}
                                </span>
                              </>
                            ) : (
                              "-"
                            )}
                          </span>
                        </td>
                        <td className="arh-relative arh-px-4 arh-py-3 arh-font-poppins arh-text-xs arh-font-medium arh-text-darkGreyColor">
                          <div className="arh-flex arh-items-center arh-justify-between arh-space-x-2">
                            <button
                              type="button"
                              onClick={() => onOpenDraft(draft)}
                              className="arh-h-6 arh-shrink-0 arh-whitespace-nowrap arh-rounded-[4px] arh-border arh-border-solid arh-border-greenColor arh-bg-[#E6F8EF] arh-px-3 arh-font-poppins arh-text-xs arh-font-medium arh-leading-none arh-text-greenColor"
                            >
                              {t("Compléter")}
                            </button>
                            <Menu
                              withIcon
                              itemClass="!arh-w-[8rem] !arh-min-w-0"
                              lists={[
                                {
                                  icon: <SvgBlackTrash />,
                                  title: "Effacer",
                                  click: () => onDeleteDraft(draft)
                                }
                              ]}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7}>
                        <EmptyData>{t("Aucun élément")}</EmptyData>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Disclosure.Panel>
          </div>
        )}
      </Disclosure>

      <div className="arh-h-full arh-max-h-[calc(100dvh-82px)] arh-w-full arh-overflow-auto arh-border-x arh-border-y-0 arh-border-solid arh-border-transparent">
        <table className="arh-w-full arh-min-w-[1100px] arh-table-fixed arh-font-poppins">
          <ExpenseColumns />
          <thead className="arh-sticky arh-top-0 arh-z-[5] arh-bg-greyColor">
            <tr className="arh-border-x-0 arh-border-b arh-border-t-0 arh-border-solid arh-border-borderTable">
              <th className="arh-w-[16%] arh-px-4 arh-py-3 arh-text-xs arh-font-semibold arh-capitalize arh-text-darkColor">
                {t("Catégorie")}
              </th>
              <th className="title_table_employer arh-w-[11%] arh-px-4 arh-py-3.5 arh-text-xs arh-font-semibold arh-capitalize arh-text-darkColor">
                {t("Montant")}
              </th>
              <th className="title_table_employer arh-w-[16%] arh-px-4 arh-py-3.5 arh-text-xs arh-font-semibold arh-capitalize arh-text-darkColor">
                {t("Date")}
              </th>
              <th className="title_table_employer arh-w-[11%] arh-px-4 arh-py-3.5 arh-text-xs arh-font-semibold arh-capitalize arh-text-darkColor">
                {t("Description")}
              </th>
              <th className="title_table_employer arh-w-[11%] arh-px-4 arh-py-3.5 arh-text-xs arh-font-semibold  arh-capitalize arh-text-darkColor ">
                {t("Commentaire")}
              </th>
              <th className="arh-px-4 arh-py-3.5 arh-text-left arh-text-xs arh-font-semibold arh-capitalize arh-text-darkColor">
                {t("Fichier joint")}
              </th>
              <th className="arh-w-[22%] arh-px-4 arh-py-3.5 arh-text-xs arh-font-semibold arh-capitalize arh-text-darkColor tablet-850:arh-px-2">
                {t("Statut")}
              </th>
            </tr>
          </thead>
          <tbody>
            {filterLoading || !reimbursmentsList?.isLoaded ? (
              <tr>
                <td colSpan={7}>
                  <div className="arh-h-[40dvh]">
                    <DotLoader size="small" />
                  </div>
                </td>
              </tr>
            ) : reimbursmentsFiltered?.length ? (
              reimbursmentsFiltered?.map((reimbursment) => (
                <React.Fragment key={reimbursment._id}>
                  <Items
                    reimbursment={reimbursment}
                    t={t}
                    i18n={i18n}
                  />
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan={7}>
                  <EmptyData>{t("Aucun résultat")}</EmptyData>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ModalNewRequest
        show={showModalRequest}
        onClickClose={selectedDraft ? onCloseDraft : onClickNewRequest}
        reimbursment={selectedDraft || {}}
      />

      <ModalMileinageRequest
        isOpen={showMileageReimbursment}
        toggle={onAddClickMileageReimbursment}
        onClickClose={onAddClickMileageReimbursment}
        t={t}
      />
    </>
  );
});
export default Index;
